import React, { useEffect, useMemo, useRef } from "react"
import { GlobalSidebar, SidebarList, FormInputAntd, AntdButton } from "components"
import { yupResolver } from "@hookform/resolvers/yup"
import { CreatePayGroupsInterface } from "@type/payGroup.types"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { SDPayGroupInitialValues, SDPayGroupSchema } from "Schema/StaticData/SDPayGroup"
import {
	getPayGroup,
	isPayGroupLoading,
	isPayGroupSaving,
	savePayGroup,
	selectPayGroupById,
	selectPayGroupData,
} from "store/PayGroups/payGroup.slice"
import { useAppDispatch, useAppSelector } from "hooks"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface PayGroupTypeFormHandler extends CreatePayGroupsInterface {}

const SDPayGroup = () => {
	const dispatch = useAppDispatch()
	const saving = useAppSelector(isPayGroupSaving())
	const loading = useAppSelector(isPayGroupLoading())
	const SDPayGroupList = useAppSelector(selectPayGroupData())
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSDPayGroup = "" } = params
	const SDPayGroup = useAppSelector(selectPayGroupById(selectedSDPayGroup))
	const isNewBeingCreated = useRef(false)
	const methods = useForm<PayGroupTypeFormHandler>({ resolver: yupResolver(SDPayGroupSchema) })
	const {
		handleSubmit,
		reset,
		formState: { isSubmitted, isDirty, isSubmitting },
	} = methods

	const sidebarSDPayGroup = useMemo(() => {
		return (SDPayGroupList ?? [])?.map(SDPayGroup => ({
			_id: SDPayGroup._id,
			name: SDPayGroup.name,
		}))
	}, [saving, SDPayGroupList?.length, isSubmitted])

	const onSubmit: SubmitHandler<PayGroupTypeFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			savePayGroup(data, (id: string) => {
				setSearchParams({ selected: id })
				reset({ ...data })
			}),
		)
	}

	useEffect(() => {
		dispatch(getPayGroup())
	}, [])

	useEffect(() => {
		if (!selectedSDPayGroup && !!SDPayGroupList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDPayGroupList?.[0]?._id })
		}
	}, [params, SDPayGroupList])

	useEffect(() => {
		if (selectedSDPayGroup && SDPayGroup) {
			reset({ ...SDPayGroupInitialValues, ...SDPayGroup })
		} else if (!selectedSDPayGroup && !isNewBeingCreated.current) {
			reset({ ...SDPayGroupInitialValues })
		}
	}, [selectedSDPayGroup, SDPayGroup])

	const handleSelected = (SDPayGroupId: string): void => {
		setSearchParams({ selected: SDPayGroupId })
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"Pay Groups"}
					loading={loading}
					selected={selectedSDPayGroup}
					setSelected={handleSelected}
					list={sidebarSDPayGroup.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="flex w-1/4 flex-col gap-y-2">
						<FormInputAntd type="text" label="Pay Group Name" name="name" />
						<div className=" flex gap-2">
							<AntdButton
								onClick={() => {
									isNewBeingCreated.current = true
									reset({ ...SDPayGroupInitialValues })
									setSearchParams({ selected: "" })
								}}
							>
								New
							</AntdButton>
							<AntdButton disabled={!isDirty || isSubmitting} loading={saving} htmlType="submit">
								{selectedSDPayGroup ? "Update" : "Save"}
							</AntdButton>
						</div>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDPayGroup
